import React, { useState, useEffect, useRef } from "react";
import {
  Bell,
  CheckCheck,
  Clock,
  AlertCircle,
  Info,
  Calendar,
  Award,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useParams } from "react-router-dom";
import { notificationsAPI } from "../services/api";
import { useApp } from "../contexts/AppContext";
import "./Notifications.css";

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

const Notifications = () => {
  const { eventId } = useParams();
  const { setUnreadCount } = useApp();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const isFetchingRef = useRef(false);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchNotifications();

    // Set up polling
    pollIntervalRef.current = setInterval(() => {
      pollNotifications();
    }, POLL_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [eventId]);

  const fetchNotifications = async () => {
    // Race condition prevention: don't fetch if already fetching
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);
    try {
      const response = await notificationsAPI.getAll(
        eventId ? parseInt(eventId) : null,
      );
      setNotifications(response.data || []);
      updateUnreadCountFromList(response.data || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const pollNotifications = async () => {
    // Race condition prevention
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    try {
      // Get last notification ID for efficient polling
      const lastId =
        notifications.length > 0
          ? Math.max(...notifications.map((n) => n.id))
          : null;

      const response = await notificationsAPI.getAll(
        eventId ? parseInt(eventId) : null,
        lastId,
      );

      const newNotifications = response.data || [];

      if (newNotifications.length > 0) {
        // Prepend new notifications
        setNotifications((prev) => [...newNotifications, ...prev]);
        updateUnreadCountFromList([...newNotifications, ...notifications]);
      }
    } catch (error) {
      console.error("Failed to poll notifications:", error);
    } finally {
      isFetchingRef.current = false;
    }
  };

  const updateUnreadCountFromList = (notifList) => {
    const unread = notifList.filter((n) => !n.is_read).length;
    setUnreadCount(unread);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n,
        ),
      );
      updateUnreadCountFromList(
        notifications.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n,
        ),
      );
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "reminder":
        return <Clock size={20} />;
      case "alert":
        return <AlertCircle size={20} />;
      case "masterclass":
        return <Award size={20} />;
      case "schedule":
        return <Calendar size={20} />;
      default:
        return <Info size={20} />;
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case "reminder":
        return "var(--rf-primary)";
      case "alert":
        return "var(--rf-warning)";
      case "masterclass":
        return "var(--rf-accent)";
      case "schedule":
        return "var(--rf-success)";
      default:
        return "var(--rf-info)";
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return timestamp;
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  if (loading) {
    return (
      <div className="notifications-page">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="notification-skeleton">
            <div className="skeleton icon-skeleton" />
            <div style={{ flex: 1 }}>
              <div
                className="skeleton"
                style={{ height: 18, width: "70%", marginBottom: 8 }}
              />
              <div className="skeleton" style={{ height: 14, width: "90%" }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="notifications-page">
      {unreadCount > 0 && (
        <div className="notifications-header">
          <span>{unreadCount} unread</span>
          <button onClick={handleMarkAllAsRead}>
            <CheckCheck size={16} />
            Mark all as read
          </button>
        </div>
      )}

      {notifications.length > 0 ? (
        <div className="notifications-list">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-item ${notification.is_read ? "read" : "unread"}`}
              onClick={() =>
                !notification.is_read && handleMarkAsRead(notification.id)
              }
            >
              <div
                className="notification-icon"
                style={{
                  backgroundColor: `${getIconColor(notification.notification_type)}15`,
                  color: getIconColor(notification.notification_type),
                }}
              >
                {getIcon(notification.notification_type)}
              </div>
              <div className="notification-content">
                <h4>{notification.title}</h4>
                <p>{notification.message}</p>
                <span className="notification-time">
                  {formatTimestamp(notification.created_at)}
                </span>
              </div>
              {!notification.is_read && <div className="unread-dot" />}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Bell size={32} />
          </div>
          <h3>No notifications</h3>
          <p>You're all caught up!</p>
        </div>
      )}
    </div>
  );
};

export default Notifications;
